import { NormalBlending, type Scene } from 'three';
import { VisualRandom } from '../../visual/index.js';
import type {
  QualityContext,
  WaveEvent,
  VisualFrameContext,
  VisualMutation,
  VisualRenderer,
  VisualSignalEvent,
} from '../../visual/index.js';
import { InstancedField, NOISE_GLSL } from './materials/InstancedField.js';

/**
 * Sumi-e.
 *
 * An earlier version drew a sheet of paper and put ink on it. That was wrong
 * twice over. It filled the frame with a bright rectangle — a visible plane
 * with visible edges, which reads as a mask laid over the work rather than as a
 * ground — and it forced every mark to fade outward into *white*, so each
 * stroke wore a pale halo that no brush has ever produced.
 *
 * There is no paper here now. The ink carries its own substance and fades to
 * **transparent**, and the material is recognisable from how it behaves rather
 * than from what it sits on:
 *
 *   - **A dense core that thins outward.** Alpha falls faster than tone lifts,
 *     so the spine dominates and the margin disappears instead of glowing. Over
 *     a bright layer the same profile darkens what is beneath it, which is what
 *     ink does; against the dark sky it reads as the shape of a stroke.
 *   - **The brush has pressure.** A stroke is not a lozenge: it lands, bears
 *     down, releases and lifts, and its width follows that along its length.
 *   - **The paper is felt, not drawn.** Fibre breaks the mark up along its
 *     travel, the edge wicks irregularly, and pigment granulates just inside the
 *     boundary. The sheet is implied by what the ink does at its margins.
 *   - **Loading is uneven.** A brush carries more ink at the start of a stroke
 *     than at the end, and 濃墨 to 淡墨 is a real tonal range rather than an
 *     opacity slider.
 */

const INK_VERTEX = /* glsl */ `
attribute vec4 aTransform;  // x, y, size, angle
attribute vec4 aLife;       // born, lifetime, seed, kind
attribute vec4 aColor;      // core tone rgb, loading
attribute vec4 aShape;      // wetness, dryness, granulation, stretch

uniform float uTime;

varying vec2 vUv;
varying vec4 vLife;
varying vec4 vColor;
varying vec4 vShape;
varying float vAge;

void main() {
  vUv = uv;
  vLife = aLife;
  vColor = aColor;
  vShape = aShape;

  float lifetime = max(aLife.y, 0.0001);
  vAge = clamp((uTime - aLife.x) / lifetime, 0.0, 1.0);

  float alive = 1.0 - step(aTransform.z, 0.0);
  float stretch = max(aShape.w, 0.2);
  vec2 local = vec2(position.x * stretch, position.y / sqrt(stretch)) * aTransform.z * alive;

  float c = cos(aTransform.w);
  float s = sin(aTransform.w);
  vec2 rotated = vec2(local.x * c - local.y * s, local.x * s + local.y * c);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(aTransform.xy + rotated, 0.0, 1.0);
}
`;

const INK_FRAGMENT = /* glsl */ `
precision highp float;

${NOISE_GLSL}

uniform float uTime;
uniform float uDetail;
uniform float uBleed;
uniform float uFibre;

varying vec2 vUv;
varying vec4 vLife;
varying vec4 vColor;
varying vec4 vShape;
varying float vAge;

void main() {
  vec2 p = (vUv - 0.5) * 2.0;
  float seed = vLife.z;
  float kind = vLife.w;
  float wetness = vShape.x;
  float dryness = vShape.y;
  float granulation = vShape.z;

  float d;
  float along = 0.5;

  if (kind < 0.5) {
    // Stroke. Drawn on a square quad, with its half-width written directly in
    // quad units rather than produced by stretching the geometry.
    //
    // The stretch is what defeated the earlier attempts. Squashing the quad
    // along y compresses everything expressed in y by the same factor, so a bend
    // of a quarter of the stroke's width came out as a fraction of a pixel and
    // the spine stayed dead straight however large the number was made. And a
    // straight tapered shape is a splinter, whatever its taper looks like — the
    // passage kept reading as broken glass. On a square quad the bend is in the
    // same units as the length and survives.
    //
    // Writing the width directly also bounds the mark: the furthest it can reach
    // is bend + waver + halfWidth * (1 + 0.4 * feather), which is well inside the
    // quad for every value below, so this kind needs no containment scaling.
    along = p.x * 0.5 + 0.5;
    // The brush lands almost at full width and drags out to nothing. Ramping in
    // over a fifth of the travel made it symmetric, and symmetric is a needle.
    // Both ends reach exactly zero before the quad's edge — a floor under the
    // pressure kept the spine opaque at along 0 and 1 and chopped the tip and the
    // heel square, however soft the sides were.
    float attack = smoothstep(0.0, 0.06, along);
    float release = 1.0 - smoothstep(0.55, 0.99, along);
    float pressure = attack * pow(release, 0.5);
    // One arc per stroke, its sign and depth from the seed so it keeps its own
    // bend for its whole life. An arm sweeping a brush cannot draw a straight
    // line, and this is most of what separates a stroke from a shard.
    float bend = (fract(seed * 0.317) - 0.5) * 0.8;
    float spine = bend * (along * (1.0 - along) * 4.0 - 0.5);
    // Waver on top, so it is not a drawn arc either.
    spine += (fbm(vec2(along * 4.0, seed * 9.0), 2) - 0.5) * 0.16;
    d = abs(p.y - spine) / max(0.13 * pressure, 0.0006);
  } else if (kind < 1.5) {
    // Pool: an irregular boundary. Ink finds the sheet's weaknesses rather than
    // spreading in a circle.
    float wobble = fbm(p * 2.4 + seed * 13.0, int(uDetail)) - 0.5;
    d = length(p) + wobble * 0.4 * wetness;
  } else {
    // Splatter: torn and spiky.
    float spikes = fbm(p * 6.0 + seed * 7.0, int(uDetail)) - 0.5;
    d = length(p) + spikes * 0.7;
  }

  // Wicking. The edge is irregular because the fibres are, and the feather
  // widens with how loaded the brush was.
  float wickAmp = min(0.2, 0.3 * wetness * uBleed);
  float wick = (fbm(p * 3.1 + seed * 4.7, int(uDetail)) - 0.5) * wickAmp;
  float feather = clamp(mix(0.26, 0.7, wetness) * uBleed, 0.18, 0.85);

  // Keep the whole soft edge inside the quad.
  //
  // Anything past a radius of 1 is outside the geometry and simply not drawn, so
  // whatever reaches further comes back with a straight cut along it. Two things
  // push outward and both have to be paid for: the fade, which ends at
  // 1 + 0.4 * feather, and the noise, which displaces the boundary outward by up
  // to 'slack' before the fade even begins. Hand-picked multipliers per mark kind
  // tracked neither — they ignored uBleed, so ULTRA clipped where LIVE did not,
  // and they ignored the wobble, so a wet pool clipped where a dry one did not.
  // Solving for the outermost point the shape can actually reach holds for all
  // three kinds at every quality, and leaves 0.94 as the margin.
  float slack = wickAmp * 0.5;
  float contain = 1.0;
  if (kind >= 0.5) {
    slack += kind < 1.5 ? 0.2 * wetness : 0.35;
    contain = (1.0 + feather * 0.4) / max(0.94 - slack, 0.3);
  }
  d *= contain;

  float core = 1.0 - smoothstep(1.0 - feather, 1.0 + feather * 0.4, d + wick);
  if (core <= 0.0) discard;

  // Fibre breakup: the sheet's tooth strips ink from a fast or dry brush, along
  // the direction of travel.
  // Two scales: coarse streaks along the travel, and a finer tooth. At a
  // single scale the strokes stayed smooth blades however high dryness went.
  float streak = fbm(vec2(p.x * 6.0, p.y * 1.4) + seed * 3.0, int(uDetail));
  float tooth = fbm(vec2(p.x * 22.0, p.y * 5.0) + seed * 8.0, int(uDetail));
  float breakup = smoothstep(0.34, 0.72, streak) * 0.75 + smoothstep(0.42, 0.7, tooth) * 0.45;
  core *= max(1.0 - dryness * breakup * uFibre * 1.1, 0.0);

  // Uneven loading: more ink at the start of a stroke than at the end.
  float loading = mix(1.0, 0.55, along) * 0.65 + 0.35;

  // Granulation just inside the rim, where pigment collects as water leaves.
  float rim = smoothstep(0.5, 1.0, d) * (1.0 - smoothstep(1.0, 1.2, d));
  float settled = 1.0 + granulation * rim * 0.85;

  // Dark core, diluted margin, transparent edge.
  //
  // Both halves matter. The tone lifts towards the dilute end as the mark thins,
  // which is what a wash does; the alpha falls faster than the tone lifts, so
  // the margin vanishes rather than becoming a pale ring. Nothing here ever
  // moves towards white.
  float density = pow(core, 1.25) * loading * settled;
  // Dilute ink is *less* present than heavy ink, and on this near-black ground
  // that means dimmer and cooler — not brighter. Lifting the margin above the
  // core was the halo the paper plane used to produce, rebuilt in the shader:
  // every overlap climbed towards pale and the passage went chalky.
  vec3 dilute = vColor.rgb * vec3(0.5, 0.53, 0.62);
  vec3 tone = mix(dilute, vColor.rgb, clamp(core * 1.15, 0.0, 1.0));

  float set = smoothstep(0.0, 0.3, vAge);
  float alpha = clamp(density * vColor.a * mix(0.8, 1.0, set), 0.0, 1.0);
  if (alpha < 0.004) discard;

  gl_FragColor = vec4(tone, alpha);
}
`;

type MarkKind = 'stroke' | 'pool' | 'splatter';

interface Mark {
  active: boolean;
  x: number;
  y: number;
  born: number;
  lifetime: number;
  size: number;
  angle: number;
  stretch: number;
  kind: MarkKind;
  wetness: number;
  dryness: number;
  granulation: number;
  /** 0..1, where 1 is the heaviest ink. */
  concentration: number;
  seed: number;
  spread: number;
}

export interface SumiRendererOptions {
  readonly scene: Scene;
  readonly layerId: string;
  readonly fieldRadius: number;
  readonly capacity?: number;
}

export class SumiRenderer implements VisualRenderer {
  readonly id = 'SUMI';

  private readonly scene: Scene;
  private readonly fieldRadius: number;
  private readonly baseCapacity: number;
  private capacity: number;
  private detail = 3;
  private bleedScale = 1;
  private fibreScale = 1;

  private field: InstancedField | null = null;
  private marks: Mark[] = [];
  private enabled = true;

  private brushWidth = 1;
  private brushCurvature = 0;
  private inkLoad = 0.7;
  private dryness = 0.15;
  private drying = 0;
  private lastStrokeAngle = 0;
  private lastInputTime = 0;

  private pending: {
    strength: number;
    kind: MarkKind;
    /** Field units. Absent means "wherever this renderer likes". */
    at?: { x: number; y: number; angle: number };
    /** A wave's own stream, so its marks do not depend on the layer's. */
    stream?: VisualRandom;
    dryer?: number;
  }[] = [];
  private wavesDrawn = 0;
  private readonly mutationCounts: Record<string, number> = {};
  private lastTime = 0;

  constructor(private readonly options: SumiRendererOptions) {
    this.scene = options.scene;
    this.fieldRadius = options.fieldRadius;
    this.baseCapacity = options.capacity ?? 80;
    this.capacity = this.baseCapacity;
  }

  setQuality(quality: QualityContext): void {
    if (this.field === null) {
      this.capacity = Math.max(12, Math.round(this.baseCapacity * quality.capacityScale));
    }
    this.detail = Math.max(2, Math.min(6, Math.round(2 + quality.iterationScale * 1.4)));
    this.bleedScale = quality.preset === 'LIVE' ? 0.85 : quality.preset === 'HIGH' ? 1.1 : 1.35;
    this.fibreScale = quality.preset === 'LIVE' ? 0.7 : quality.preset === 'HIGH' ? 1 : 1.3;
    this.field?.setUniform('uDetail', this.detail);
    this.field?.setUniform('uBleed', this.bleedScale);
    this.field?.setUniform('uFibre', this.fibreScale);
  }

  initialize(context: VisualFrameContext): void {
    if (this.field === null) {
      this.field = new InstancedField({
        scene: this.scene,
        name: `visual:SUMI:${this.options.layerId}`,
        capacity: this.capacity,
        vertexShader: INK_VERTEX,
        fragmentShader: INK_FRAGMENT,
        // Alpha over whatever is beneath. Multiply was the obvious choice while
        // this renderer owned a sheet of paper; without one it would multiply
        // the sky by a dark colour and produce nothing at all.
        blending: NormalBlending,
        renderOrder: 7,
        uniforms: {
          uDetail: { value: this.detail },
          uBleed: { value: this.bleedScale },
          uFibre: { value: this.fibreScale },
        },
      });
      this.marks = Array.from({ length: this.capacity }, () => emptyMark());
    }
    this.field.attach();
    this.lastTime = context.time;
  }

  reset(): void {
    for (const mark of this.marks) mark.active = false;
    this.pending.length = 0;
    this.field?.clearAll();
    this.brushWidth = 1;
    this.inkLoad = 0.7;
    this.dryness = 0.15;
    this.drying = 0;
    this.wavesDrawn = 0;
    for (const key of Object.keys(this.mutationCounts)) delete this.mutationCounts[key];
  }

  handleSignal(event: VisualSignalEvent): void {
    // The beat draws. A pool here produced one merged stain with no brush in it.
    if (event.name === 'BEAT') this.queue(event.strength * 0.6, 'stroke');
  }

  applyMutation(item: VisualMutation, context: VisualFrameContext): void {
    this.mutationCounts[item.action] = (this.mutationCounts[item.action] ?? 0) + 1;
    const struck = !item.sustained;
    const force = item.sustained ? item.strength * context.deltaTime : item.strength;

    switch (item.action) {
      case 'SPAWN':
        if (struck) {
          this.inkLoad = Math.min(1, this.inkLoad + 0.15);
          this.queue(item.strength, 'stroke');
        }
        break;
      case 'SPLASH':
        // A miss is ink thrown at the sheet, not a worse stroke.
        if (struck) for (let index = 0; index < 6; index += 1) this.queue(item.strength, 'splatter');
        break;
      case 'BLEED':
      case 'DISTORT':
        this.inkLoad = Math.min(1, this.inkLoad + force * 0.5);
        if (struck) this.queue(item.strength, 'pool');
        break;
      case 'BREAK':
        this.dryness = Math.min(0.95, this.dryness + force * 0.5);
        if (struck) this.queue(item.strength, 'stroke');
        break;
      case 'SHIFT':
        if (struck) this.queue(item.strength, 'splatter');
        break;
      case 'ROTATE':
        this.brushCurvature += force * 1.4;
        break;
      case 'EXPAND':
      case 'DENSITY':
        this.brushWidth = Math.min(3, this.brushWidth + force * 0.4);
        break;
      case 'CONTRACT':
        this.brushWidth = Math.max(0.3, this.brushWidth - force * 0.4);
        break;
      case 'FREEZE':
        this.drying = Math.min(1, this.drying + force * 0.7);
        break;
      case 'RELEASE':
        this.drying = Math.max(0, this.drying - force * 0.7);
        this.inkLoad = Math.min(1, this.inkLoad + force * 0.4);
        break;
      default:
        break;
    }
  }

  update(context: VisualFrameContext): void {
    const field = this.field;
    if (field === null || !this.enabled) return;

    const delta = Math.min(0.1, Math.max(0, context.time - this.lastTime));
    this.lastTime = context.time;
    const signals = context.signals;

    this.brushWidth += (0.6 + signals.BASS * 2.0 - this.brushWidth) * Math.min(1, delta * 2);
    this.brushCurvature += (signals.MID * 2 - this.brushCurvature) * Math.min(1, delta * 1.5);
    this.inkLoad += (0.35 + signals.AMPLITUDE * 1.4 - this.inkLoad) * Math.min(1, delta * 1.2);

    // Silence dries the sheet: marks stop spreading and the brush runs dry.
    const quiet = context.time - this.lastInputTime;
    if (quiet > 2.5) this.drying = Math.min(1, this.drying + delta * 0.22);
    else this.drying = Math.max(0, this.drying - delta * 0.5);
    this.dryness += (this.drying * 0.65 + signals.TREBLE * 0.2 - this.dryness) * Math.min(1, delta);

    if (signals.TREBLE > 0.55 && context.random.bool(delta * signals.TREBLE * 1.2)) {
      this.queue(signals.TREBLE, 'splatter');
    }

    for (const request of this.pending) this.spawn(context, request);
    this.pending.length = 0;

    const opacity = context.layer.opacity * context.layer.intensity;

    let live = 0;
    for (const mark of this.marks) {
      if (!mark.active) continue;
      const age = (context.time - mark.born) / mark.lifetime;
      if (age >= 1) {
        mark.active = false;
        continue;
      }

      const wetness = mark.wetness * (1 - this.drying * 0.7);
      if (mark.kind !== 'stroke') mark.size += mark.spread * delta * wetness * (1 - age);

      // Ink sets rather than fades; only the very end of a life releases it.
      const settle = age > 0.85 ? 1 - (age - 0.85) / 0.15 : 1;
      const tone = inkTone(mark.concentration);

      field.write(
        live,
        [mark.x, mark.y, mark.size, mark.angle],
        [mark.born, mark.lifetime, mark.seed, kindCode(mark.kind)],
        [tone[0], tone[1], tone[2], clamp01(mark.concentration * settle * opacity)],
        [clamp01(wetness), clamp01(mark.dryness), mark.granulation, mark.stretch],
      );
      live += 1;
      if (live >= field.capacity) break;
    }

    field.setTime(context.time);
    field.commit(live);
  }

  private spawn(
    context: VisualFrameContext,
    request: {
      strength: number;
      kind: MarkKind;
      at?: { x: number; y: number; angle: number };
      stream?: VisualRandom;
      dryer?: number;
    },
  ): void {
    const mark = this.marks.find((candidate) => !candidate.active);
    if (mark === undefined) return;
    const { strength, kind } = request;
    // A wave brings its own stream so that the same event draws the same mark
    // however much work this layer had already done when it arrived.
    const random = request.stream ?? context.random;
    const reach = this.fieldRadius * numeric(context.layer.parameters.reach, 0.62);
    if (request.at !== undefined) {
      // Field units in, world units out. The wave layer works in field units so
      // that a spawn log means the same thing at 720p and at 4K.
      request = {
        ...request,
        at: {
          x: request.at.x * this.fieldRadius,
          y: request.at.y * this.fieldRadius,
          angle: request.at.angle,
        },
      };
    }

    mark.active = true;
    mark.born = context.time;
    mark.kind = kind;
    mark.seed = random.next() * 100;
    // Biased upward: a brush is rarely perfectly loaded, and without enough
    // dryness the fibre breakup never fires and every mark is a smooth blade.
    mark.dryness = clamp01(this.dryness * 0.7 + random.range(0.18, 0.62) + (request.dryer ?? 0));
    mark.granulation = random.range(0.25, 0.8);
    mark.wetness = clamp01(this.inkLoad * random.range(0.7, 1.2));
    this.lastInputTime = context.time;

    switch (kind) {
      case 'stroke':
        // Where a stroke sits and which way it points are independent choices;
        // deriving one from the other put every brush in a central starburst.
        this.lastStrokeAngle +=
          this.brushCurvature * random.range(0.2, 0.6) + random.range(-0.5, 0.5);
        mark.angle = request.at?.angle ?? this.lastStrokeAngle;
        mark.x = request.at?.x ?? random.signed() * reach;
        mark.y = request.at?.y ?? random.signed() * reach * 0.8;
        mark.size = this.fieldRadius * 0.2 * this.brushWidth * random.range(0.75, 1.4);
        // Square. The stroke's length and width are both written in quad units, so
        // stretching the geometry would only compress its bend away again.
        mark.stretch = 1;
        mark.lifetime = random.range(4, 8.5);
        // A wide tonal range, drawn per stroke. Every stroke landing in the same
        // narrow band read as one flat grey; 濃墨 and 淡墨 next to each other is
        // most of what makes a sumi passage look like painting rather than
        // handwriting.
        mark.concentration = clamp01(random.range(0.18, 1.0) * (0.65 + strength * 0.45));
        mark.spread = 0;
        break;
      case 'pool':
        mark.angle = request.at?.angle ?? random.range(0, Math.PI * 2);
        mark.x = request.at?.x ?? random.signed() * reach;
        mark.y = request.at?.y ?? random.signed() * reach;
        mark.size = this.fieldRadius * random.range(0.13, 0.3) * (0.6 + strength);
        mark.stretch = random.range(0.85, 1.25);
        mark.lifetime = random.range(8, 16);
        mark.concentration = clamp01(0.45 + strength * 0.3);
        mark.spread = this.fieldRadius * random.range(0.012, 0.032);
        break;
      default:
        mark.angle = request.at?.angle ?? random.range(0, Math.PI * 2);
        mark.x = request.at?.x ?? random.signed() * reach * 1.2;
        mark.y = request.at?.y ?? random.signed() * reach * 1.2;
        mark.size = this.fieldRadius * random.range(0.05, 0.17);
        mark.stretch = random.range(0.7, 2.6);
        mark.lifetime = random.range(6, 14);
        mark.concentration = clamp01(0.6 + strength * 0.35);
        mark.spread = this.fieldRadius * 0.008;
        break;
    }
  }

  private queue(strength: number, kind: MarkKind): void {
    if (this.pending.length > 20) return;
    this.pending.push({ strength, kind });
  }

  /**
   * A wave, as ink.
   *
   * An impact, and then what the sheet does with it: a loaded pool where it
   * landed, a stroke dragged out along the way it was travelling, and
   * splatter thrown ahead of it. Not "a ripple, in black" — the wave gives
   * this renderer a place and a direction and nothing else, and what arrives
   * is a brush hitting paper.
   *
   * Every number comes from a stream seeded by the event, not from the
   * layer's. A wave has to look the same whether or not the brush happened to
   * be busy when it arrived.
   */
  spawnWave(event: WaveEvent): void {
    const stream = new VisualRandom(event.seed);
    const at = { x: event.x, y: event.y, angle: event.direction };
    const force = clamp01(event.strength) * event.scale;

    // The impact. A torn wave lands drier, so it breaks up as it spreads.
    const dryer = event.style === 'torn' ? 0.3 : event.style === 'stray' ? 0.15 : 0;
    if (event.style !== 'stray') {
      this.pending.push({ strength: force, kind: 'pool', at, stream, dryer });
    }

    // Dragged out along the direction of travel.
    this.pending.push({
      strength: force,
      kind: 'stroke',
      at: { x: at.x, y: at.y, angle: at.angle },
      stream,
      dryer,
    });

    // Thrown ahead of it. A miss throws more, and further.
    const thrown = event.style === 'torn' ? 5 : event.style === 'stray' ? 1 : 2;
    for (let index = 0; index < thrown; index += 1) {
      const spray = at.angle + stream.signed() * 0.9;
      const reach = stream.range(0.06, 0.24) * event.scale;
      this.pending.push({
        strength: force,
        kind: 'splatter',
        at: { x: at.x + Math.cos(spray) * reach, y: at.y + Math.sin(spray) * reach, angle: spray },
        stream,
        dryer,
      });
    }
    this.wavesDrawn += 1;
  }

  render(): void {
    // Drawn by the stage's single render.
  }

  setParameters(): void {
    // Read per frame from the resolved layer state.
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (this.field !== null) this.field.visible = enabled;
    if (!enabled) this.reset();
  }

  dispose(): void {
    this.field?.dispose();
    this.field = null;
    this.marks = [];
  }

  get metrics(): {
    readonly live: number;
    readonly capacity: number;
    readonly inkLoad: number;
    readonly dryness: number;
    readonly drying: number;
    readonly brushWidth: number;
    readonly waves: number;
    readonly mutations: Readonly<Record<string, number>>;
  } {
    return {
      live: this.field?.liveCount ?? 0,
      capacity: this.capacity,
      inkLoad: Number(this.inkLoad.toFixed(3)),
      dryness: Number(this.dryness.toFixed(3)),
      drying: Number(this.drying.toFixed(3)),
      brushWidth: Number(this.brushWidth.toFixed(3)),
      waves: this.wavesDrawn,
      mutations: { ...this.mutationCounts },
    };
  }
}

/**
 * The ink's core tone at a given concentration.
 *
 * These are the *core* values; the shader drops them towards a dilute margin as
 * a mark thins.
 *
 * The scale runs the way the ground demands rather than the way a sheet of paper
 * would. On white, 濃墨 is the darkest thing in the frame; on this near-black sky
 * a dark value is a hole, and mapping heavy ink to near-black made every loaded
 * stroke disappear and left only the watered ones visible — the tonal range
 * inverted. What has to survive is the *relation*: heavy ink is the most present
 * mark and dilute ink the least. So concentration runs from a dim cool wash to a
 * strong neutral, staying under the bloom threshold at the top so the heaviest
 * stroke reads as ink rather than as a light source.
 */
function inkTone(concentration: number): [number, number, number] {
  const t = clamp01(concentration);
  return [
    0.11 + (0.38 - 0.11) * t,
    0.12 + (0.39 - 0.12) * t,
    0.16 + (0.44 - 0.16) * t,
  ];
}

function kindCode(kind: MarkKind): number {
  return kind === 'stroke' ? 0 : kind === 'pool' ? 1 : 2;
}

function emptyMark(): Mark {
  return {
    active: false, x: 0, y: 0, born: 0, lifetime: 1, size: 0, angle: 0, stretch: 1,
    kind: 'stroke', wetness: 0.5, dryness: 0.2, granulation: 0.4, concentration: 0.6,
    seed: 0, spread: 0,
  };
}

function numeric(value: number | string | boolean | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}
